import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import { useCuentas, useCuentaMutations } from '../../../graphql/tesoreria';

export default function ListadoCuentas() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    moneda: '',
    estado: '',
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState('banco');
  const [sortOrder, setSortOrder] = useState('asc');

  // Cargar y guardar filtros en localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem('tesoreria_cuentas_filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(parsed);
      } catch (e) {
        // Ignorar errores de parsing
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('tesoreria_cuentas_filters', JSON.stringify(filters));
  }, [filters]);

  // TODO: Usar el hook real cuando esté conectado el backend
  const [result, refetch] = useCuentas({
    search: searchTerm,
    estado: filters.estado,
    moneda: filters.moneda,
    page,
    pageSize,
    sortBy,
    sortOrder,
  });

  const { deleteCuenta } = useCuentaMutations();

  const cuentas = result.data?.cuentasBancarias?.data || [];
  const pagination = result.data?.cuentasBancarias?.pagination || {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleDelete = async (cuenta) => {
    if (
      window.confirm(
        `¿Está seguro de eliminar la cuenta "${cuenta.banco} - ${cuenta.descripcion}"?`
      )
    ) {
      try {
        await deleteCuenta({ id: cuenta.id });
        // TODO: Mostrar toast de éxito
        alert('Cuenta eliminada correctamente');
        refetch();
      } catch (error) {
        // TODO: Mostrar toast de error
        alert('Error al eliminar la cuenta');
        console.error(error);
      }
    }
  };

  const handleDuplicate = (cuenta) => {
    navigate('/Tesoreria/Cuentas/Nueva', {
      state: {
        duplicateFrom: {
          ...cuenta,
          id: undefined,
          descripcion: `${cuenta.descripcion} (Copia)`,
          saldo: 0,
        },
      },
    });
  };

  const exportToCSV = () => {
    const headers = ['Banco', 'Descripción', 'Moneda', 'Tipo', 'CBU', 'Alias', 'Saldo', 'Estado'];
    const rows = cuentas.map((cuenta) => [
      cuenta.banco,
      cuenta.descripcion,
      cuenta.moneda,
      cuenta.tipo,
      cuenta.cbu,
      cuenta.alias,
      cuenta.saldo,
      cuenta.estado,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cuentas_bancarias_${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <PageHeader
        title="Cuentas Bancarias"
        subtitle="Gestión de cuentas bancarias de la empresa"
        actions={[
          <button
            key="nueva"
            onClick={() => navigate('/Tesoreria/Cuentas/Nueva')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Nueva Cuenta
          </button>,
        ]}
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        {/* Panel principal */}
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          {/* Topbar con búsqueda y filtros */}
          <div className="p-4 border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel))]">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Búsqueda */}
              <div className="flex-1 relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por banco, CBU, alias..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Buscar cuentas"
                />
              </div>

              {/* Filtros */}
              <div className="flex gap-2">
                <select
                  value={filters.moneda}
                  onChange={(e) => setFilters({ ...filters, moneda: e.target.value })}
                  className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Filtrar por moneda"
                >
                  <option value="">Todas las monedas</option>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>

                <select
                  value={filters.estado}
                  onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                  className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Filtrar por estado"
                >
                  <option value="">Todos los estados</option>
                  <option value="ACTIVO">Activo</option>
                  <option value="INACTIVO">Inactivo</option>
                </select>

                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Exportar a CSV"
                >
                  <Download className="w-4 h-4" aria-hidden="true" />
                  Exportar
                </button>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
                <tr>
                  <th
                    onClick={() => handleSort('banco')}
                    className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider cursor-pointer hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Banco {getSortIcon('banco')}
                  </th>
                  <th
                    onClick={() => handleSort('descripcion')}
                    className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider cursor-pointer hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Descripción {getSortIcon('descripcion')}
                  </th>
                  <th
                    onClick={() => handleSort('moneda')}
                    className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider cursor-pointer hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Moneda {getSortIcon('moneda')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                    CBU / Alias
                  </th>
                  <th
                    onClick={() => handleSort('saldo')}
                    className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider cursor-pointer hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Saldo {getSortIcon('saldo')}
                  </th>
                  <th
                    onClick={() => handleSort('estado')}
                    className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider cursor-pointer hover:text-[rgb(var(--fg))] transition-colors"
                  >
                    Estado {getSortIcon('estado')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--border))]">
                {cuentas.length > 0 ? (
                  cuentas.map((cuenta) => (
                    <tr
                      key={cuenta.id}
                      className="hover:bg-[rgba(var(--muted),0.2)] transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-[rgb(var(--fg))]">
                        {cuenta.banco}
                      </td>
                      <td className="px-4 py-3 text-sm text-[rgb(var(--fg))]">
                        {cuenta.descripcion}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[rgba(var(--brand),0.1)] text-[rgb(var(--brand))]">
                          {cuenta.moneda}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-[rgb(var(--fg))]">
                          {cuenta.alias || cuenta.cbu}
                        </div>
                        {cuenta.alias && cuenta.cbu && (
                          <div className="text-xs text-[rgb(var(--muted))] mt-0.5">
                            {cuenta.cbu}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-[rgb(var(--fg))]">
                        {formatCurrency(cuenta.saldo)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            cuenta.estado === 'ACTIVO'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}
                        >
                          {cuenta.estado === 'ACTIVO' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              navigate(`/Tesoreria/Cuentas/${cuenta.id}/Ver`)
                            }
                            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            aria-label="Ver detalles"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" aria-hidden="true" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/Tesoreria/Cuentas/${cuenta.id}/Editar`)
                            }
                            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            aria-label="Editar"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" aria-hidden="true" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(cuenta)}
                            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            aria-label="Duplicar"
                            title="Duplicar"
                          >
                            <Copy className="w-4 h-4" aria-hidden="true" />
                          </button>
                          <button
                            onClick={() => handleDelete(cuenta)}
                            className="p-1.5 rounded-lg hover:bg-[rgba(var(--muted),0.5)] text-[rgb(var(--muted))] hover:text-red-600 dark:hover:text-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            aria-label="Eliminar"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Filter
                          className="w-12 h-12 text-[rgb(var(--muted))]"
                          aria-hidden="true"
                        />
                        <p className="text-[rgb(var(--muted))]">
                          No se encontraron cuentas bancarias
                        </p>
                        <button
                          onClick={() => navigate('/Tesoreria/Cuentas/Nueva')}
                          className="mt-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors"
                        >
                          Crear primera cuenta
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <div className="p-4 border-t border-[rgb(var(--border))] flex items-center justify-between">
              <div className="text-sm text-[rgb(var(--muted))]">
                Mostrando {(page - 1) * pageSize + 1} a{' '}
                {Math.min(page * pageSize, pagination.total)} de {pagination.total}{' '}
                cuentas
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                </button>
                <span className="px-4 py-2 text-sm text-[rgb(var(--fg))]">
                  Página {page} de {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="p-2 rounded-lg border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  aria-label="Página siguiente"
                >
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
